import { getAsyncTokenCookie, getLoggedInStatus, getMetaCookies, USER_UUID_COOKIE_NAME } from '@root/utils/cookies/cookies';
import isBusinessEmail from '@root/utils/isBusinessEmail';
import { mapUUIDToBucket } from '@root/utils/uuidHelpers';
import crypto from 'crypto';
import mixpanel from 'mixpanel-browser';
import { GrowthState } from 'scribe-networking/types';
import { v4 as uuidv4 } from 'uuid';

import { getAccountQueryOptions } from '@/_api/account/getAccount';
import { getQueryClient } from '@/_lib/getQueryClient';
import { FullScribeUser } from '@/_types/Account';
import { SmartEmbedCustomizationViewsEnum } from '@/_types/SmartEmbedCustomizations';
import { getRuntimeEnv } from '@/_utils/environment';

import { fetcher } from './fetcher';

const { isProduction } = getRuntimeEnv();

export const hashString = (stringInput: string): string | null => {
  if (!stringInput) return null;
  const normalizedString = stringInput.trim().toLowerCase();
  const hash = crypto.createHash('sha256');

  hash.update(normalizedString);
  return hash.digest('hex');
};

const NO_PERSIST_TRACKING_PATHS = ['embed', 'page-embed'];

export const shouldDisableTrackingPersistance = (route?: string) => {
  if (!route) return false;
  const pathSegment = route.split('/')[1];

  return pathSegment ? NO_PERSIST_TRACKING_PATHS.includes(pathSegment) : false;
};

// Function to decode JWT token
const decodeToken = (token: string | undefined) => {
  if (!token) return null;
  const base64Url = token.split('.')[1];

  if (!base64Url) return null;
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  let payload;

  if (typeof window !== 'undefined') {
    // Client side
    payload = JSON.parse(window.atob(base64));
  } else {
    // Server side
    payload = JSON.parse(Buffer.from(base64, 'base64').toString('utf8'));
  }
  return payload;
};

// Function to get user ID from token
const getUserId = async () => {
  const { token } = await getAsyncTokenCookie();
  const payload = decodeToken(token);
  const userId = payload?.user_id || null;

  return userId;
};

const shouldTrack = () =>
  isProduction && (!global.window || (global.window.location.origin && !global.window.location.origin.includes('localhost')));

type TrackService = {
  trackPageView: (page: string) => Promise<any>;
  trackCopyLink: (documentId: string, location: string) => Promise<any>;
  trackCopyEmbed: (documentId: string, format: SmartEmbedCustomizationViewsEnum) => Promise<any>;
  trackCioEvent: (eventName: string, eventFields: Record<string, any>) => Promise<any>;
  trackNewRelic: (message: string, attributes: Record<string, any>) => Promise<any>;
};

export const trackService: TrackService = {
  trackPageView: async (page: string) => {
    return fetcher.post(`track/page_view/`, {
      body: { page }
    });
  },
  trackCopyLink: async (documentId: string, location: string) => {
    return fetcher.post(`track/copy_link/`, {
      body: {
        document_id: documentId,
        location
      }
    });
  },
  trackCopyEmbed: async (documentId: string, format: SmartEmbedCustomizationViewsEnum) => {
    return fetcher.post(`track/copy_embed/`, {
      body: {
        document_id: documentId,
        format
      }
    });
  },
  trackCioEvent: async (eventName: string, eventFields: Record<string, any>) => {
    return fetcher.post(`track/cio/`, {
      body: {
        event_name: eventName,
        event_fields: eventFields
      }
    });
  },
  trackNewRelic: async (message: string, attributes: Record<string, any>) => {
    const userId = await getUserId();

    if (shouldTrack()) {
      return fetcher.post<Promise<any>>(`track/log_new_relic/`, {
        body: { user_id: userId, attributes, message },
        isTextResponse: true
      });
    }
    // eslint-disable-next-line no-console
    console.log(`Would track a NewRelic log: ${message}: ${JSON.stringify(attributes)} in production.`);
  }
};

const CIO_EVENTS = [
  'add_task',
  'show_feature_gate_modal',
  'extension_dropdown_open_recommended_clicked',
  'joined_community_waitlist',
  'click_share_modal_tab',
  'opened_workflow_analyst'
];
const CAPI_TRACKED_ACTIONS_MAP = { shared_first_scribe: 'First Scribe Share' };
const NEW_RELIC_EVENTS = ['account_created', 'onboarding_completed'];
const NEW_RELIC_PATHS = ['/signup', '/workspace', '/welcome'];

// Function to get or generate a user UUID
const getUserUUID = () => {
  const storedValue = typeof localStorage !== 'undefined' ? localStorage.getItem(USER_UUID_COOKIE_NAME) : null;
  let userUUID = storedValue ? JSON.parse(storedValue) : null;

  if (!userUUID) {
    userUUID = uuidv4();
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('user-Uuid', JSON.stringify(userUUID));
    }
  }
  return JSON.stringify(userUUID);
};

export const fireCAPIEvent = async (eventFields: Record<string, any>) => {
  return fetcher.post('track/capi/', {
    headers: {
      'Content-Type': 'application/json'
    },
    body: eventFields
  });
};

export const getPersonalInfoParams = (account: FullScribeUser | null) => {
  if (!account) return null;

  const { email, first_name: firstName, last_name: lastName } = account;

  const userInfoParams = {
    email_sha: hashString(email),
    first_name_sha: firstName ? hashString(firstName) : null,
    last_name_sha: lastName ? hashString(lastName) : null
  };

  return userInfoParams;
};

/**
 *
 * @param {string} eventName
 * @param {Record<string, any>=} details - Custom properties to include with the event
 * @param {string=} eventCategory
 * @param {string=} eventAction
 */
export const trackEvent = async (
  eventName: string,
  details: Record<string, any> = {},
  eventCategory: string | null = null,
  eventAction: string | null = null
) => {
  const queryClient = getQueryClient();

  const { isLoggedIn } = getLoggedInStatus();
  const account = isLoggedIn ? await queryClient.fetchQuery(getAccountQueryOptions()) : null;

  // Handle details objects that "aren't extensible."
  const payload = {
    ...details,
    is_electron: false,
    version: 'refactor',
    super_organization_id: account?.active_organization?.super_organization?.id,
    active_organization_id: account?.active_organization?.id,
    current_plan_atm: account?.active_organization?.super_organization?.plan?.name || 'free',
    current_pro_plan_type_atm: account?.active_organization?.super_organization?.pro_plan_type,
    current_active_team_size: account?.active_organization?.user_count,
    version_history_enabled: account?.active_organization?.super_organization?.version_history_enabled
  };

  if (shouldTrack()) {
    mixpanel.track(eventName, payload);

    const currentUserId = account?.id;

    // GTM tracking, forwarding key events to third-party systems
    if (eventCategory && eventAction) {
      if (eventAction in CAPI_TRACKED_ACTIONS_MAP) {
        const eventId = uuidv4();
        const userInfoParams = getPersonalInfoParams(account);
        const { fbc, fbp } = await getMetaCookies();

        fireCAPIEvent({
          event_name: CAPI_TRACKED_ACTIONS_MAP[eventAction as keyof typeof CAPI_TRACKED_ACTIONS_MAP],
          event_id: eventId,
          external_id: currentUserId,
          ...userInfoParams,
          fbc,
          fbp
        })
          .then(() => trackEvent('capi_tracking_successful', { event: 'signup', event_id: eventId, ...userInfoParams }))
          .catch(() => trackEvent('capi_tracking_failed', { event: 'signup', event_id: eventId, ...userInfoParams }));
        if (typeof window !== 'undefined' && window?.dataLayer) {
          window.dataLayer.push({
            event: 'GTMevent',
            eventCategory,
            eventAction,
            eventLabel: eventName,
            userId: currentUserId,
            metaExternalId: currentUserId,
            metaEventId: eventId,
            ...userInfoParams
          });
        }
      } else if (typeof window !== 'undefined' && window?.dataLayer) {
        window.dataLayer.push({
          event: 'GTMevent',
          eventCategory,
          eventAction,
          eventLabel: eventName,
          userId: currentUserId
        });
      }
    }

    // Customer IO tracking
    if (CIO_EVENTS.includes(eventName)) {
      trackService.trackCioEvent(eventName, details);
    }

    // New Relic tracking
    const eventPath = details?.path;

    if (NEW_RELIC_EVENTS.includes(eventName) || (eventPath && NEW_RELIC_PATHS.includes(eventPath))) {
      trackService.trackNewRelic(eventName, details);
    }
  } else {
    // eslint-disable-next-line no-console
    console.log(`Would track event: ${eventName}: ${JSON.stringify(payload)} in production.`);
  }
};

export const trackExperiment = async (experimentName: string, variantValue: string | boolean, details: Record<string, any> = {}) => {
  if (variantValue !== undefined) {
    await trackEvent('view_experiment', {
      experiment: experimentName,
      variant: String(variantValue),
      ...details
    });
  }
};

const windowUnloaded = () => {
  trackEvent('window_unloaded');
};

export const trackSession = (window: Window) => {
  trackEvent('window_loaded');

  window.addEventListener('unload', windowUnloaded);

  return () => {
    window.removeEventListener('unload', windowUnloaded);
  };
};

export const trackSignup = async (signupMethod: string, userId: string, account?: FullScribeUser) => {
  if (shouldTrack()) {
    const { token } = await getAsyncTokenCookie();
    const tokenPayload = decodeToken(token);
    const currentUserId = tokenPayload?.user_id || userId || getUserUUID();
    const userInfoParams = account ? getPersonalInfoParams(account) : null;
    const eventId = uuidv4();
    const { fbc, fbp } = await getMetaCookies();

    if (typeof window !== 'undefined' && window?.dataLayer) {
      window.dataLayer.push({
        event: 'GTMevent',
        eventCategory: 'account',
        eventAction: 'signup',
        eventLabel: signupMethod,
        userId: currentUserId,
        metaExternalId: currentUserId,
        metaEventId: eventId,
        ...userInfoParams
      });
    }
    fireCAPIEvent({
      event_name: 'Lead',
      event_id: eventId,
      external_id: currentUserId,
      ...userInfoParams,
      fbc,
      fbp
    })
      .then(() => trackEvent('capi_tracking_successful', { event: 'signup', event_id: eventId, ...userInfoParams }))
      .catch(() => trackEvent('capi_tracking_failed', { event: 'signup', event_id: eventId, ...userInfoParams }));
  } else {
    // eslint-disable-next-line no-console
    console.log('Would track signup');
  }
};

/**
 *
 * @param {Record<string, any>=} body
 */
export const trackAccountCreatedForGoogleSSO = async (body: Record<string, any>) => {
  if (shouldTrack()) {
    await trackEvent('account_created', body);
  } else {
    // eslint-disable-next-line no-console
    console.log('Would track account created for Google SSO');
  }
};

export const trackSignIn = async (signInMethod: string, userId: string) => {
  if (shouldTrack()) {
    if (typeof window !== 'undefined' && window?.dataLayer) {
      const { token } = await getAsyncTokenCookie();
      const tokenPayload = decodeToken(token);
      const currentUserId = tokenPayload?.user_id || userId;

      window.dataLayer.push({
        event: 'GTMevent',
        eventCategory: 'account',
        eventAction: 'signin',
        eventLabel: signInMethod,
        userId: currentUserId
      });
    }
  } else {
    // eslint-disable-next-line no-console
    console.log('Would track signin');
  }
};

export const trackFirstScribeCreation = async (scribeId: string) => {
  if (shouldTrack()) {
    await trackEvent(
      'created_first_scribe',
      {
        file_id: scribeId
      },
      'action',
      'first_scribe_created'
    );
  } else {
    // eslint-disable-next-line no-console
    console.log('Would track created first scribe');
  }
};

export const getTimestampFromDate = (date: string) => {
  if (date) {
    return Math.round(Date.parse(date) / 1000);
  }
  return null;
};

const createMixpanelUser = (user: FullScribeUser, userGrowthState: GrowthState) => {
  const bucket = user ? mapUUIDToBucket(user.id) : undefined;
  const mixpanelUser = {
    USER_ID: user?.id,
    'Sign up date': user?.date_joined,
    'First name': user?.first_name,
    'Last name': user?.last_name,
    bucket,
    $email: user?.email,
    business_email: isBusinessEmail(user?.email),
    super_organization_id: user?.active_organization?.super_organization?.id,
    plan: user?.active_organization?.super_organization?.plan.name,
    subscription_status: user?.active_organization?.super_organization?.stripe_subscription_status,
    installed_extension: userGrowthState?.installed_extension,
    is_enterprise_self_serve: user?.active_organization?.super_organization?.enterprise_plan_type === 'self_serve',
    extension_currently_pinned: userGrowthState?.extension_currently_pinned,
    pro_plan_type: user?.active_organization?.super_organization?.pro_plan_type,
    active_team_size: user?.active_organization?.user_count,
    signup_method: user?.signup_method,
    signup_source: userGrowthState?.signup_source ?? undefined
  };

  return mixpanelUser;
};

export const trackUser = (user: FullScribeUser, userGrowthState: GrowthState) => {
  const mixpanelUser = createMixpanelUser(user, userGrowthState);

  if (shouldTrack()) {
    mixpanel.identify(user.id);
    mixpanel.people.set(mixpanelUser);
  } else {
    if (!user) {
      // eslint-disable-next-line no-console
      console.warn('Null user passed to tracking code.');
    }
    // eslint-disable-next-line no-console
    console.log(`Would track user: ${JSON.stringify(mixpanelUser)} in production. Created at ${getTimestampFromDate(user.date_joined)}`);
  }
};
